

import { useCallback, useEffect, useMemo, useState } from 'react';

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const ORDER: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl'];

const QUERIES: Record<Exclude<Breakpoint, 'xs'>, string> = {
  sm: '(min-width:600px)',
  md: '(min-width:900px)',
  lg: '(min-width:1200px)',
  xl: '(min-width:1536px)',
};

function useMatches(query: string) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mql = window.matchMedia(query);
    const update = () => setMatches(mql.matches);

    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, [query]);

  return matches;
}

export function useResponsiveValue() {
  const isSm = useMatches(QUERIES.sm);
  const isMd = useMatches(QUERIES.md);
  const isLg = useMatches(QUERIES.lg);
  const isXl = useMatches(QUERIES.xl);

  const activeIndex = useMemo(() => {
    if (isXl) return 4;
    if (isLg) return 3;
    if (isMd) return 2;
    if (isSm) return 1;
    return 0; // xs
  }, [isSm, isMd, isLg, isXl]);

  return useCallback(
    <T>(map: Partial<Record<Breakpoint, T>>, defaultValue?: T): T => {
      // recorremos hacia abajo desde el breakpoint actual
      for (let i = activeIndex; i >= 0; i--) {
        const key = ORDER[i];
        if (map[key] !== undefined) {
          return map[key] as T;
        }
      }
      return defaultValue as T;
    },
    [activeIndex]
  );
}
